/* GetNails: independent, accessible carousels for images in product cards. */
(() => {
  'use strict';
  if (customElements.get('gn-card-carousel')) return;

  class GNCardCarousel extends HTMLElement {
    connectedCallback() {
      if (this._ready || this._connectFrame) return;
      // Custom elements can be attached while their contents are still parsing.
      this._connectFrame = requestAnimationFrame(() => {
        this._connectFrame = null;
        if (this.isConnected) this.initialize();
      });
    }

    initialize() {
      if (this._ready) return;
      this.track = this.querySelector('[data-gn-card-track]');
      this.slides = Array.from(this.querySelectorAll('[data-gn-card-slide]'));
      this.prevButton = this.querySelector('[data-gn-card-prev]');
      this.nextButton = this.querySelector('[data-gn-card-next]');
      this.counter = this.querySelector('[data-gn-card-counter]');
      this.progress = this.querySelector('[data-gn-card-progress]');
      this.currentIndex = 0;
      if (!this.track || this.slides.length < 2) return;
      this._ready = true;
      this._hoverPreview = false;
      this.updateProgress();
      this._controller = new AbortController();
      const signal = this._controller.signal;

      this.track.addEventListener('scroll', () => {
        if (this._scrollFrame) return;
        this._scrollFrame = requestAnimationFrame(() => {
          this._scrollFrame = null;
          this.setIndex(Math.round(this.track.scrollLeft / (this.track.clientWidth || 1)));
        });
      }, { passive: true, signal });

      for (const [button, step] of [[this.prevButton, -1], [this.nextButton, 1]]) {
        if (!button) continue;
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.cancelHoverPreview();
          this.goTo(this.currentIndex + step);
        }, { signal });
        for (const name of ['touchstart', 'pointerdown']) {
          button.addEventListener(name, (event) => event.stopPropagation(), { passive: true, signal });
        }
      }

      // Desktop-only: hovering the FIRST image previews the second.
      // Leaving the gallery restores the first image unless the visitor has
      // deliberately used an arrow, drag, trackpad or keyboard navigation.
      const viewport = this.querySelector('.gn-card-carousel__viewport');
      viewport?.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' ||
            !matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)').matches ||
            this.currentIndex !== 0 || this._mouseDrag) return;
        this._hoverPreview = true;
        this.goTo(1);
      }, { signal });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse' || !this._hoverPreview) return;
        this._hoverPreview = false;
        this.goTo(0);
      }, { signal });

      // Don't let parent Swiper sections steal horizontal gestures made on the image.
      for (const name of ['touchstart', 'touchmove', 'pointerdown', 'pointermove']) {
        this.track.addEventListener(name, (event) => event.stopPropagation(), { passive: true, signal });
      }
      // Touch still uses native scrolling; mouse drag now gets the same image-by-image gesture.
      this.track.addEventListener('pointerdown', (event) => {
        this.pointerStartX = event.clientX;
        this.pointerStartY = event.clientY;
        if (event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary) return;
        this._mouseDrag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          startLeft: this.track.scrollLeft,
          startIndex: this.currentIndex,
          dragging: false
        };
      }, { passive: true, signal });

      this.track.addEventListener('pointermove', (event) => {
        const drag = this._mouseDrag;
        if (!drag || event.pointerId !== drag.pointerId) return;
        if (!(event.buttons & 1)) {
          this.finishMouseDrag(event, true);
          return;
        }
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        if (!drag.dragging) {
          // Keep ordinary clicks and small, mostly vertical movements intact.
          if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) return;
          drag.dragging = true;
          this.cancelHoverPreview();
          this.track.classList.add('gn-card-carousel__track--dragging');
          this.track.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        this.track.scrollLeft = drag.startLeft - dx;
      }, { signal });

      this.track.addEventListener('pointerup', (event) => {
        this.finishMouseDrag(event);
        // Keep the existing click protection for finger swipes.
        if (event.pointerType !== 'mouse' &&
            (Math.abs(event.clientX - (this.pointerStartX ?? event.clientX)) > 12 ||
             Math.abs(event.clientY - (this.pointerStartY ?? event.clientY)) > 12)) {
          this.draggedAt = Date.now();
        }
      }, { passive: true, signal });
      this.track.addEventListener('pointercancel', (event) => this.finishMouseDrag(event, true), { signal });
      this.track.addEventListener('lostpointercapture', (event) => this.finishMouseDrag(event, true), { signal });
      // Suppress the browser's native image/link drag ghost without affecting clicks.
      this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      // Trackpad horizontal gestures should scroll this gallery, not an enclosing slider.
      this.track.addEventListener('wheel', (event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
          this.cancelHoverPreview();
          event.stopPropagation();
        }
      }, { passive: true, signal });
      this.querySelector('.gn-card-carousel__link')?.addEventListener('click', (event) => {
        if (Date.now() - (this.draggedAt || 0) < 450) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { capture: true, signal });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        event.stopPropagation();
        const direction = event.key === 'ArrowLeft' ? -1 : 1;
        this.cancelHoverPreview();
        this.goTo(this.currentIndex + (document.documentElement.dir === 'rtl' ? -direction : direction));
      }, { signal });

      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(() => {
          if (this.currentIndex > 0) {
            this.track.scrollTo({ left: this.currentIndex * this.track.clientWidth, behavior: 'instant' });
          }
        });
        this._resizeObserver.observe(this.track);
      }
    }

    disconnectedCallback() {
      if (this._connectFrame) cancelAnimationFrame(this._connectFrame);
      if (this._scrollFrame) cancelAnimationFrame(this._scrollFrame);
      this._connectFrame = this._scrollFrame = null;
      this._controller?.abort();
      this._resizeObserver?.disconnect();
      this._mouseDrag = null;
      this._hoverPreview = false;
      this.track?.classList.remove('gn-card-carousel__track--dragging');
      this._ready = false;
    }

    cancelHoverPreview() {
      this._hoverPreview = false;
    }

    finishMouseDrag(event, cancelled = false) {
      const drag = this._mouseDrag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      this._mouseDrag = null;
      if (!drag.dragging) return;

      this.draggedAt = Date.now();
      this.track.classList.remove('gn-card-carousel__track--dragging');
      const dx = event.clientX - drag.startX;
      const width = this.track.clientWidth || 1;
      const threshold = Math.max(24, Math.min(width * 0.16, 55));
      let target = drag.startIndex;
      if (!cancelled && Math.abs(dx) >= threshold) {
        const steps = Math.max(1, Math.round(Math.abs(dx) / width));
        target += dx < 0 ? steps : -steps;
      }
      // Unlike arrow navigation, dragging stops at the first/last image.
      this.goTo(Math.max(0, Math.min(this.slides.length - 1, target)));
      if (this.track.hasPointerCapture(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }
    }

    setIndex(index) {
      const next = Math.max(0, Math.min(this.slides.length - 1, index));
      if (next === this.currentIndex) return;
      this.currentIndex = next;
      if (this.counter) this.counter.textContent = `${next + 1} / ${this.slides.length}`;
      this.updateProgress();
    }

    updateProgress() {
      if (this.progress && this.slides.length) {
        this.progress.style.setProperty('--gn-card-progress', `${((this.currentIndex + 1) / this.slides.length) * 100}%`);
      }
    }

    goTo(index) {
      const next = (index + this.slides.length) % this.slides.length;
      this.setIndex(next);
      this.track.scrollTo({
        left: next * this.track.clientWidth,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
    }
  }

  customElements.define('gn-card-carousel', GNCardCarousel);
})();
